/**
 * useBookingQueue.ts
 * Manages the operator booking queue as an in-memory stateful list.
 * Drives queue progression and workflow state without any routing.
 */
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import type { BookingJob, StockReservation, BackendWorkflow } from '@/lib/types/workflow';
import type { StockStatus } from '@/lib/types/workflow';
import { WorkflowStatus, BackendWorkflowStatus, toFrontendStatus } from '@/lib/types/workflow';
import type { ExtractedGearItem, PricingLineItem } from '@/lib/types/booking';
import { getActiveWorkflows, getEmail, getEmailDrafts } from '@/lib/services/bookings';
import type { SearchFilters } from '@/components/ui/SearchBar';

function parseDisplayName(senderEmail: string): string {
  const match = senderEmail.match(/^(.+?)\s*<[^>]+>$/);
  return match ? match[1].trim() : senderEmail;
}

function parseEmailAddress(senderEmail: string): string {
  const match = senderEmail.match(/<([^>]+)>/);
  return match ? match[1] : senderEmail;
}

function formatRentalDates(dates: string[]): string {
  if (!dates.length) return '';
  const sorted = [...dates].sort();
  const days = sorted.length;
  const toDate = (s: string) => new Date(s + 'T00:00:00Z');
  const fmt = (d: Date) =>
    d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', timeZone: 'UTC' });
  if (days === 1) return `${fmt(toDate(sorted[0]))} (1 day)`;
  return `${fmt(toDate(sorted[0]))} - ${fmt(toDate(sorted[sorted.length - 1]))} (${days} days)`;
}

/** Real stock status from inventory-service's check_availability() (via the
 *  pricing line's is_available/substitute_available), not a placeholder.
 *  Before 26 Aug 2026 every matched gear item was hardcoded to 'IN_STOCK'
 *  here regardless of true quantity on hand — an operator only ever found
 *  out an item was actually unavailable later, at the separate Stock
 *  Reservation step, which produced a confusing "In Stock" -> CONFLICT flip
 *  reported live on staging. `line` is undefined when pricing hasn't
 *  resolved yet (or draft creation failed) — treated as unknown/in-stock so
 *  a still-pricing draft doesn't flash a false unavailable warning. */
function deriveStockStatus(line: PricingLineItem | undefined): StockStatus {
  if (!line) return 'IN_STOCK';
  if (!line.matched) return 'UNAVAILABLE';
  if (line.is_available) return 'IN_STOCK';
  return line.substitute_available ? 'SUBSTITUTION' : 'UNAVAILABLE';
}

/** Raw ISO start/end behind formatRentalDates()'s display string — approveAndSend()
 *  in DashboardPage needs real dates to call reserveStock(), not the formatted label.
 *  Mirrors _rentalDateRange() in DashboardPage.tsx's own (separate) enrichment path. */
function rentalDateRange(dates: string[]): { start: string; end: string } | undefined {
  if (!dates.length) return undefined;
  const sorted = [...dates].sort();
  return { start: sorted[0], end: sorted[sorted.length - 1] };
}

// Workflows in these statuses are terminal and have their own home in
// Archive (Sent / Deleted / Quarantine) — they must never appear in the
// active dashboard queue. getActiveWorkflows() below already asks the
// backend for only RECEIVED/PROCESSING/PENDING_APPROVAL, so this filter is
// now redundant defense-in-depth rather than the only thing keeping a
// terminal workflow out of `queue` — kept anyway so a future status added to
// the "active" set inconsistently on one side still can't leak a
// REJECTED/SENT/QUARANTINED card into the pager or the `?booking=` URL-jump
// effect in DashboardPage.
const TERMINAL_QUEUE_STATUSES = new Set(['REJECTED', 'SENT', 'QUARANTINED']);

export function useLiveBookingQueue(filters?: SearchFilters) {
  const [queue, setQueue] = useState<BookingJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEnriching, setIsEnriching] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // setTotal was never wired to anything — no API response field currently
  // populates it (getActiveWorkflows() returns items only, no pagination
  // total). Left as a fixed 0 rather than removing `total` from this hook's
  // return value entirely, since that's a slightly larger surface change;
  // wire up a real setter here if/when the queue needs a true total count
  // distinct from queue.length.
  const [total] = useState(0);
  const [retryKey, setRetryKey] = useState(0);
  // Drives the inbox "checking for new mail" cue: when the last successful
  // poll finished, and whether one is in flight right now.
  const [lastCheckedAt, setLastCheckedAt] = useState<number | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const isFirstLoad = useRef(true);
  // Mirrors `queue` so every 30s poll can carry forward already-enriched
  // fields (clientName, subject, gear, draft, etc.) for workflow IDs we've
  // already seen, instead of blanking them back to placeholders on every
  // tick. Without this, setQueue(jobs) below replaced the whole array with
  // 'Loading...'/empty-field placeholders every poll, which — because
  // useBookingQueue's liveQueue effect mirrors this queue immediately —
  // flashed every card and the open booking's queue position blank for the
  // ~1-3s it took the enrichment pass to patch real data back in. That was
  // the "whole page flicker every few seconds" reported by the operator.
  const queueRef = useRef<BookingJob[]>([]);
  const refetch = () => setRetryKey((k) => k + 1);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        if (isFirstLoad.current) {
          setIsLoading(true);
          setIsEnriching(true);
        }
        setError(null);
        setIsChecking(true);
        const raw = await getActiveWorkflows(filters);
        if (cancelled) return;
        setLastCheckedAt(Date.now());
        setIsChecking(false);

        const rawData = raw as unknown as { items: BackendWorkflow[] } | BackendWorkflow[];
        const allWorkflows = Array.isArray(rawData) ? rawData : rawData.items;
        const workflows = allWorkflows.filter((w) => !TERMINAL_QUEUE_STATUSES.has(w.status));

        // Reuse already-enriched fields for workflow IDs we've seen before —
        // only genuinely new IDs (just arrived since the last poll) get the
        // 'Loading...'/blank placeholder shape, since those haven't been
        // enriched yet regardless.
        const prevById = new Map(queueRef.current.map((j) => [j.id, j]));

        const jobs: BookingJob[] = workflows.map((workflow) => {
          const prev = prevById.get(workflow.id);
          const base: BookingJob = {
            id: workflow.id,
            clientName: 'Loading...',
            clientEmail: '',
            receivedAt: '',
            subject: 'Loading...',
            emailBody: '',
            status: toFrontendStatus(workflow.status as BackendWorkflowStatus),
            backendStatus: workflow.status,
            created_at: workflow.created_at,
            assignedToEmail: workflow.assigned_to_email ?? undefined,
            isThreadReply: false,
            gear: [],
            draftSubject: '',
            draftBody: '',
            aiConfidence: 0,
            location: '',
            rentalDates: '',
            quoteTotalRands: 0,
          };
          if (!prev) return base;
          // A workflow sitting at RECEIVED or PROCESSING has no valid
          // CURRENT draft — either one hasn't been created yet, or (for a
          // re-ingested booking) the draftId/draftBody/gear `prev` still
          // remembers belongs to a PREVIOUS pipeline run that the reingest
          // superseded. Carrying that stale draft forward via `...prev`
          // let an operator reach Approve/Finalize & Send on a booking the
          // backend still correctly had at RECEIVED with no current draft
          // at all — reported 07 Sep 2026 against two rejected-then-
          // reingested bookings: one read as fully drafted and reachable
          // all the way to the Finalize & Send screen (backend rejected
          // the send with "Workflow is RECEIVED, not APPROVED"), the other
          // showed a full draft body with no draftId behind it ("No draft
          // available to approve"). Root cause: if a poll cycle never
          // observes the REJECTED status in between — e.g. reject and
          // reingest both happen inside one 30s polling window — `prev`
          // still holds this id's last-known PENDING_APPROVAL-era object,
          // and the reingest's fresh RECEIVED status got the old draft
          // spread onto it regardless.
          //
          // The fix for that (reset to a blank `base` placeholder) was too
          // broad: it fired for EVERY RECEIVED/PROCESSING workflow on
          // EVERY poll, including the overwhelmingly common case of a
          // booking that was already RECEIVED/PROCESSING last poll and
          // still is now — nothing reingested, nothing to distrust. That
          // blanked the card's already-fetched clientName/subject/
          // emailBody back to 'Loading...' every 30s regardless, then the
          // enrichment pass patched real content back in a moment later —
          // reintroducing, for every unclaimed inbound card, exactly the
          // "whole page flicker" bug the `queueRef` carry-forward pattern
          // above this effect was built to eliminate. Reported again
          // 07 Sep 2026, same day as the fix that caused it ("the glitch
          // is happening again"), against a 14-card inbound queue — i.e.
          // every single card flashing on every poll.
          //
          // Only reset when this is a GENUINE regression: `prev` was
          // further along the pipeline (had a real current draft) and the
          // booking just dropped back to RECEIVED/PROCESSING — that's the
          // actual reingest signal. If `prev` was already RECEIVED/
          // PROCESSING, its email fields are still valid and safe to carry
          // forward below like every other status.
          const isGenuineRegressionToUnclaimed =
            (workflow.status === 'RECEIVED' || workflow.status === 'PROCESSING') &&
            prev.backendStatus !== 'RECEIVED' &&
            prev.backendStatus !== 'PROCESSING';
          if (isGenuineRegressionToUnclaimed) {
            return base;
          }
          // Carry forward everything already learned about this booking;
          // status/backendStatus/assignedToEmail still come fresh off this
          // poll's workflow response so claim/status changes still show up.
          return {
            ...prev,
            status: base.status,
            backendStatus: base.backendStatus,
            created_at: base.created_at,
            assignedToEmail: base.assignedToEmail,
          };
        });

        const createdAtMap = new Map(workflows.map((w) => [w.id, w.created_at]));
        jobs.sort((a, b) => new Date(createdAtMap.get(a.id)!).getTime() - new Date(createdAtMap.get(b.id)!).getTime());

        queueRef.current = jobs;
        setQueue(jobs);
        setIsLoading(false);
        isFirstLoad.current = false;

        await Promise.allSettled(
          workflows.map(async (workflow) => {
            const rawEmailId = workflow.raw_email_id;
            if (!rawEmailId) return;
            // PROCESSING has no email data yet - skip entirely.
            // RECEIVED has an email but no draft yet - fetch email only.

            const [emailResult, draftsResult] = await Promise.allSettled([
              getEmail(rawEmailId),
              workflow.status === 'RECEIVED' ? Promise.resolve([]) : getEmailDrafts(rawEmailId),
            ]);

            if (cancelled) return;

            setQueue((prev) => {
              // Look up this workflow's CURRENT position by id rather than
              // reusing its index in the `workflows` array above. `jobs`
              // (which seeded this same state, a few lines up) is sorted by
              // created_at right after being built from `workflows` — so
              // `workflows[i]` and `queue[i]` are only the same booking when
              // `workflows` already happened to arrive in created_at order.
              // getActiveWorkflows() concatenates three separately-fetched
              // status buckets (RECEIVED, then PROCESSING, then
              // PENDING_APPROVAL), so that's essentially never true once more
              // than one status is present — the old positional `next[index]
              // = ...` was silently writing one booking's freshly-fetched
              // email/draft onto a DIFFERENT booking's card. Reported
              // symptom: paging through the queue with the prev/next arrows
              // changed the Booking ID but the email content didn't change to
              // match, and several cards appeared to hang forever on "AI
              // Processing" (their real enrichment landed on someone else's
              // card instead). Matching by id is correct regardless of sort
              // order or how many status buckets got merged.
              const idx = prev.findIndex((j) => j.id === workflow.id);
              if (idx === -1) return prev;
              const next = [...prev];

              const emailUpdates =
                emailResult.status === 'fulfilled'
                  ? {
                      clientName: parseDisplayName(emailResult.value.sender_email),
                      clientEmail: parseEmailAddress(emailResult.value.sender_email),
                      receivedAt: emailResult.value.received_at ?? '',
                      subject: emailResult.value.subject,
                      emailBody: emailResult.value.body_plain ?? '',
                      isThreadReply: emailResult.value.is_thread_reply,
                      threadRootId: emailResult.value.thread_root_id ?? undefined,
                    }
                  : {};

              const draftUpdates =
                draftsResult.status === 'fulfilled' && draftsResult.value.length > 0
                  ? (() => {
                      const draft = draftsResult.value[0];
                      // pricing_line_items is built from extracted_gear in the same
                      // order (draft-service's _apply_pricing constructs the
                      // quote-pricing request directly from gear_items, and
                      // inventory-service preserves input order) — index-matching
                      // is deliberate, not name-matching, since a fuzzy RMS match
                      // can rename an item (matched_item_name != requested name).
                      const pricingLines = draft.pricing_line_items ?? [];
                      return {
                        draftId: draft.id,
                        aiConfidence: Math.round((draft.ai_confidence_score ?? 0) * 100),
                        gear: (draft.extracted_gear ?? []).map((g: ExtractedGearItem, idx: number) => ({
                          // Positional, slash-free id — NOT derived from the item name.
                          // This id becomes the reservation line's gear_item_id and is
                          // used as a URL path segment (/reservations/{id}/lines/{line_id});
                          // names like "Monitor/Recorder" or "f/2.8 Lens" contain "/" and
                          // 404 every accept/keep/substitute-candidates call.
                          id: `gear-${idx}`,
                          name: g.name,
                          quantity: g.quantity,
                          unitPrice: pricingLines[idx]?.day_rate ?? 0,
                          stockStatus: deriveStockStatus(pricingLines[idx]),
                        })),
                        location: draft.extracted_location ?? '',
                        rentalDates: formatRentalDates(draft.extracted_dates ?? []),
                        rentalDateRange: rentalDateRange(draft.extracted_dates ?? []),
                        draftBody: draft.body_plain ?? '',
                        draftSubject: draft.subject ?? '',
                        // total_price is the sum of real line_totals, no VAT
                        // applied — matches what quoteTotalRands has always
                        // meant to FinalizeView/QuoteSummary's own *1.15 math.
                        quoteTotalRands: draft.total_price ?? 0,
                        priceTier: draft.price_tier ?? null,
                      };
                    })()
                  : {};

              next[idx] = { ...next[idx], ...emailUpdates, ...draftUpdates };
              queueRef.current = next;
              return next;
            });
          }),
        );

        if (!cancelled) setIsEnriching(false);
      } catch (err: unknown) {
        if (cancelled) return;
        setIsChecking(false);
        // Only surface the full-page "Something went wrong" error on a
        // genuine first-load failure (we have nothing to show yet). A
        // background 30s refresh that hits a transient blip (a token
        // needing refresh, a momentary 502 from the gateway) must NOT nuke
        // an operator's open booking — LiveDashboardLoader swaps the whole
        // dashboard for the error screen whenever `error` is set, so doing
        // that on every failed background poll produced a "whole page
        // flicker" every time staging had a brief hiccup. Keep serving the
        // last-known-good queue and quietly retry on the next tick instead.
        if (isFirstLoad.current) {
          setError(err instanceof Error ? err.message : 'Failed to load workflows');
          setIsLoading(false);
          setIsEnriching(false);
        } else {
          console.warn('[useLiveBookingQueue] background refresh failed, keeping last-known queue:', err);
        }
      }
    }

    load();

    // Auto-refresh every 30 seconds to pick up new inbound emails
    const refreshInterval = setInterval(() => {
      cancelled = false;
      load();
    }, 30000);

    return () => {
      cancelled = true;
      clearInterval(refreshInterval);
    };
    // Deliberately depend on the individual filter fields rather than the
    // `filters` object itself — SearchFilters is rebuilt with a new object
    // identity by its caller on every render regardless of whether any
    // actual value changed, and depending on the object would re-run this
    // 30s poll effect (tearing down and rebuilding its interval) far more
    // often than any filter value genuinely changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters?.search, filters?.date_from, filters?.date_to, retryKey]);

  return { queue, isLoading, isEnriching, error, total, refetch, lastCheckedAt, isChecking };
}

export function useBookingQueue(initialQueue: BookingJob[], liveQueue?: BookingJob[]) {
  const [queue, setQueue] = useState<BookingJob[]>(initialQueue);
  // Sync with live enrichment updates from useLiveBookingQueue
  useEffect(() => {
    if (liveQueue && liveQueue.length > 0) {
      setQueue(liveQueue);
    }
  }, [liveQueue]);

  // currentBookingId (not a raw array index) is the single source of truth
  // for which booking the operator is looking at. useLiveBookingQueue fully
  // rebuilds and re-sorts `queue` on every 30s poll, and getActiveWorkflows()
  // stops fetching a workflow the moment it passes PENDING_APPROVAL — so a
  // fixed index could previously end up silently pointing at a different,
  // unrelated booking after a poll landed mid-review (most dangerously right
  // after Approve). DashboardPage's resync effect had no way to tell that
  // apart from a real operator navigation, so `booking.id` used by
  // finalizeSendWorkflow() could get silently swapped to the wrong workflow
  // — reproduced live 14 Sep 2026 as the FINALIZE-on-a-RECEIVED-booking 409.
  // Tracking by id instead means a background poll can never change which
  // booking is "current" out from under the operator. See
  // [[finding-injozi-approved-stall-swallowed-exception]] for the fix
  // writeup.
  const [currentBookingId, setCurrentBookingId] = useState<string | null>(initialQueue[0]?.id ?? null);

  // Falls back to the last object we actually had for an id, so the booking
  // stays fully populated (not just "not found") in the moment it drops out
  // of the live-polled `queue` — e.g. the instant after Approve, before the
  // next poll's response settles the workflow into whatever state it's
  // moving through next.
  const lastKnownBookingsRef = useRef<Map<string, BookingJob>>(new Map());
  useEffect(() => {
    queue.forEach((job) => lastKnownBookingsRef.current.set(job.id, job));
  }, [queue]);

  // Last position we could actually resolve currentBookingId to within
  // `queue` — used as the navigation base (advance/prev/goToIndex) when the
  // current booking has momentarily dropped out of the polled queue, so
  // "next" still means "next after where the operator actually was" rather
  // than snapping back to the start.
  const lastResolvedIndexRef = useRef(0);
  useEffect(() => {
    if (currentBookingId === null) return;
    const idx = queue.findIndex((j) => j.id === currentBookingId);
    if (idx !== -1) lastResolvedIndexRef.current = idx;
  }, [queue, currentBookingId]);

  const currentBooking = useMemo(() => {
    if (currentBookingId === null) return null;
    return queue.find((j) => j.id === currentBookingId) ?? lastKnownBookingsRef.current.get(currentBookingId) ?? null;
  }, [queue, currentBookingId]);

  // Recomputed for display only (progress "X / Y", prev/next enablement in
  // ActionBar/DashboardPage) — falls back to the last resolved position
  // rather than -1 so those consumers don't see a nonsensical value during
  // the brief window a booking is between polls.
  const queueIndex = useMemo(() => {
    if (currentBookingId === null) return queue.length;
    const idx = queue.findIndex((j) => j.id === currentBookingId);
    if (idx !== -1) return idx;
    return Math.min(lastResolvedIndexRef.current, Math.max(queue.length - 1, 0));
  }, [queue, currentBookingId]);

  const isQueueEmpty = currentBookingId === null;

  // Always land on the inbound queue on a fresh mount (login, cold-load with
  // no ?booking= in the URL) — the previous default of initialQueue[0]?.status
  // meant a fresh dashboard load skipped the queue entirely and dropped the
  // operator straight into whatever the oldest booking's status happened to
  // be (e.g. DRAFTED/APPROVED), rather than the inbox list. A genuine
  // mid-session reload on a specific booking still restores correctly — that
  // happens via the separate `?booking=` URL-jump effect in DashboardPage,
  // which runs after this initial state and overrides it when appropriate.
  const [workflowStatus, setWorkflowStatus] = useState<WorkflowStatus>(WorkflowStatus.PROCESSING);
  const [reservation, setReservation] = useState<StockReservation | null>(null);

  const statusForJob = useCallback(
    (job: BookingJob | undefined) =>
      job?.backendStatus
        ? toFrontendStatus(job.backendStatus as BackendWorkflowStatus)
        : WorkflowStatus.PENDING_REVIEW,
    [],
  );

  // advanceQueue() moves to the next booking after a successful Finalize &
  // Send. It used to hardcode workflowStatus to PENDING_REVIEW regardless of
  // that next booking's real backend status — correct only when the next
  // booking happens to already be PENDING_APPROVAL (toFrontendStatus maps
  // that to PENDING_REVIEW), wrong for anything else (e.g. a still-RECEIVED
  // or PROCESSING booking next in the queue, which this would have shown as
  // reviewable/approvable when it isn't actually there yet). goToPrev/
  // goToIndex/resetQueue already derive workflowStatus correctly via
  // statusForJob(queue[nextIndex]) — advanceQueue now does the same instead
  // of assuming PENDING_REVIEW. See claude/session-handoff.md, "Older
  // investigation notes" under the FINALIZE/RECEIVED section, for the
  // original flag.
  //
  // All navigation below sets currentBookingId explicitly (picking which id
  // to move to via the current array position, since navigation is a
  // deliberate one-time action) rather than storing a raw index that gets
  // silently reinterpreted against a future, different `queue` array.
  const advanceQueue = useCallback(() => {
    const baseIndex = queue.findIndex((j) => j.id === currentBookingId);
    const fromIndex = baseIndex !== -1 ? baseIndex : lastResolvedIndexRef.current;
    const nextIndex = fromIndex + 1;
    const nextJob = queue[nextIndex];
    setCurrentBookingId(nextJob?.id ?? null);
    setWorkflowStatus(statusForJob(nextJob));
    setReservation(null);
  }, [queue, currentBookingId, statusForJob]);

  // Always-current view of `queue` for callbacks that run later than the
  // render that created them (setTimeout after "Sent", async claim polling) —
  // advanceQueue/goToIndex above close over the `queue` of the render they
  // were created in, which is stale by the time those fire.
  const queueRef = useRef<BookingJob[]>(queue);
  queueRef.current = queue;

  // Ids the operator has finished with this session (sent / rejected). The
  // live queue can keep listing them as PENDING_APPROVAL until its next 30s
  // poll, so they must never be offered as "the next email to review".
  const completedIdsRef = useRef<Set<string>>(new Set());

  // After a booking is sent or rejected: move straight to the next booking
  // that is ready for review (PENDING_APPROVAL), going forward from the
  // finished one and wrapping round to the start. If nothing is ready for
  // review, land on the inbound queue while other bookings remain, and only
  // fall through to "Queue Complete" when nothing is left at all.
  const advanceAfterCompletion = useCallback((completedId: string) => {
    completedIdsRef.current.add(completedId);
    const current = queueRef.current;
    const remaining = current.filter((j) => !completedIdsRef.current.has(j.id));
    const completedIdx = current.findIndex((j) => j.id === completedId);
    const ordered =
      completedIdx === -1
        ? remaining
        : [
            ...current.slice(completedIdx + 1).filter((j) => !completedIdsRef.current.has(j.id)),
            ...current.slice(0, completedIdx).filter((j) => !completedIdsRef.current.has(j.id)),
          ];
    const nextReviewable = ordered.find((j) => j.backendStatus === 'PENDING_APPROVAL' || j.backendStatus === 'DRAFTED');

    setReservation(null);
    if (nextReviewable) {
      setCurrentBookingId(nextReviewable.id);
      setWorkflowStatus(WorkflowStatus.PENDING_REVIEW);
      return;
    }
    if (remaining.length > 0) {
      setCurrentBookingId(remaining[0].id);
      setWorkflowStatus(WorkflowStatus.PROCESSING);
      return;
    }
    setCurrentBookingId(null);
    setWorkflowStatus(WorkflowStatus.PENDING_REVIEW);
  }, []);

  const goToPrev = useCallback(() => {
    const baseIndex = queue.findIndex((j) => j.id === currentBookingId);
    const fromIndex = baseIndex !== -1 ? baseIndex : lastResolvedIndexRef.current;
    const nextIndex = Math.max(0, fromIndex - 1);
    const nextJob = queue[nextIndex];
    setCurrentBookingId(nextJob?.id ?? null);
    setWorkflowStatus(statusForJob(nextJob));
    setReservation(null);
  }, [queue, currentBookingId, statusForJob]);

  // Arrow navigation. Changed 28 Oct 2026 (operator request): the prev/next
  // arrows step to the adjacent booking in the dashboard queue whatever its
  // state, instead of skipping everything that isn't ready for review. Only
  // bookings the operator has already finished this session (sent/rejected,
  // possibly still listed until the next poll) are skipped.
  //
  // Each target opens on the screen its own backend status maps to — review
  // for DRAFTED/PENDING_APPROVAL, stock check for APPROVED, finalize for
  // RESERVATION_CONFIRMED. The pre-review states (RECEIVED / PROCESSING /
  // FAILED) map to the inbox list (PROCESSING), which would drop the operator
  // out of the 3-panel view, so they open on the review screen instead;
  // ActionBar swaps Edit/Reject/Approve for a Claim control there based on
  // the booking's backendStatus, so nothing can be approved before extraction.
  const isNavigable = useCallback((j: BookingJob) => !completedIdsRef.current.has(j.id), []);

  const arrowStatusForJob = useCallback(
    (job: BookingJob) => {
      const status = statusForJob(job);
      return status === WorkflowStatus.PROCESSING ? WorkflowStatus.PENDING_REVIEW : status;
    },
    [statusForJob],
  );

  const { prevBooking, nextBooking } = useMemo(() => {
    const at = queue.findIndex((j) => j.id === currentBookingId);
    const from = at !== -1 ? at : lastResolvedIndexRef.current;
    const prev = [...queue.slice(0, from)].reverse().find(isNavigable) ?? null;
    const next = queue.slice(from + 1).find(isNavigable) ?? null;
    return { prevBooking: prev, nextBooking: next };
  }, [queue, currentBookingId, isNavigable]);

  const goToNextBooking = useCallback(() => {
    if (!nextBooking) return;
    setCurrentBookingId(nextBooking.id);
    setWorkflowStatus(arrowStatusForJob(nextBooking));
    setReservation(null);
  }, [nextBooking, arrowStatusForJob]);

  const goToPrevBooking = useCallback(() => {
    if (!prevBooking) return;
    setCurrentBookingId(prevBooking.id);
    setWorkflowStatus(arrowStatusForJob(prevBooking));
    setReservation(null);
  }, [prevBooking, arrowStatusForJob]);

  const goToIndex = useCallback(
    (index: number) => {
      const nextJob = queue[index];
      setCurrentBookingId(nextJob?.id ?? null);
      setWorkflowStatus(statusForJob(nextJob));
      setReservation(null);
    },
    [queue, statusForJob],
  );

  const resetWorkflow = useCallback(() => {
    setWorkflowStatus(statusForJob(currentBooking ?? undefined));
    setReservation(null);
  }, [currentBooking, statusForJob]);

  const resetQueue = useCallback(() => {
    const firstJob = queue[0];
    setCurrentBookingId(firstJob?.id ?? null);
    setWorkflowStatus(statusForJob(firstJob));
    setReservation(null);
  }, [queue, statusForJob]);

  // Patches a single job's fields into this hook's own `queue` copy
  // immediately, without waiting for useLiveBookingQueue's next 30s poll.
  //
  // Added 22 Sep 2026 for handleClaimComplete (DashboardPage): claiming a
  // RECEIVED email produces a freshly-enriched job (real client/draft data,
  // now PENDING_APPROVAL) via a direct getWorkflow()/_enrichJob() call that
  // is well ahead of the live queue's own poll cycle. DashboardPage sets
  // `booking`/`draft` straight from that enriched job, but its booking-sync
  // effect re-syncs `booking`/`draft` from `queue.find(currentBookingId)`
  // on every non-editable-screen render — so without patching `queue` too,
  // that effect immediately overwrote the just-claimed booking with its
  // still-RECEIVED/placeholder entry from the last poll, undoing the claim
  // in the operator's own view a moment later. Falls through untouched if
  // the id isn't in `queue` at all (nothing to patch).
  const updateJobInQueue = useCallback((job: BookingJob) => {
    setQueue((prev) => {
      const idx = prev.findIndex((j) => j.id === job.id);
      if (idx === -1) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], ...job };
      return next;
    });
  }, []);

  // Selects a booking that isn't (or isn't yet) present in the live-polled
  // `queue` array at all — e.g. one fetched directly via _fetchBookingJob
  // because it has already moved past PENDING_APPROVAL and dropped out of
  // the active queue, or because the live queue's own 30s poll simply
  // hasn't picked it up yet. goToIndex/advanceQueue/goToPrev/updateJobInQueue
  // all require a real array position for this id, which doesn't exist in
  // this case — this sets currentBookingId directly instead, so
  // isQueueEmpty/queueIndex/currentBooking (via lastKnownBookingsRef below)
  // and DashboardPage's ?booking= URL-sync effect all still move together
  // with what's actually on screen, rather than only patching local
  // component state directly. That direct-local-state pattern is the exact
  // bug behind PRs #106/#107/#108 (see their comments at each fixed call
  // site) — DashboardPage's handleSelectJob fallback and its ?booking=
  // URL-restore effect were the last two call sites still doing it, both
  // fixed 28 Sep 2026 to call this instead. See
  // claude/finding-approved-booking-vanishes-on-navigation-away.md and the
  // "Queue Complete" desync reports for the full history.
  const selectExternalBooking = useCallback(
    (job: BookingJob) => {
      lastKnownBookingsRef.current.set(job.id, job);
      setCurrentBookingId(job.id);
      setWorkflowStatus(statusForJob(job));
      setReservation(null);
    },
    [statusForJob],
  );

  return {
    queue,
    currentBooking,
    queueIndex,
    queueTotal: queue.length,
    isQueueEmpty,
    workflowStatus,
    setWorkflowStatus,
    reservation,
    setReservation,
    advanceQueue,
    advanceAfterCompletion,
    goToPrev,
    goToNextBooking,
    goToPrevBooking,
    hasNextBooking: nextBooking !== null,
    hasPrevBooking: prevBooking !== null,
    goToIndex,
    resetWorkflow,
    resetQueue,
    updateJobInQueue,
    selectExternalBooking,
  };
}

